import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { UserRound, Mail, Phone, ArrowRight } from 'lucide-react';
import Button from '../Button';
import Input from '../Input';
import ModalPopup from '../ModalPopup';
import './BookMeeting.css';

/**
 * BookMeeting
 *
 * Short lead form (name / email / phone) shown before handing a customer over
 * to an external booking calendar. Composes the shared ModalPopup shell, so it
 * owns nothing but its own content: title, info copy, three inputs and the CTA.
 *
 * The parent owns what happens on submit (lead capture + redirect) — this
 * component only validates and reports the values.
 */

const DEFAULT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_PHONE_REGEX = /^\d{7,15}$/;

const BookMeeting = ({
  isOpen = false,
  onClose,
  onSubmit,
  banner = null,

  title = 'Book a meeting',
  infoText = '',

  nameLabel = 'Name',
  emailLabel = 'Email',
  phoneLabel = 'Phone',
  submitLabel = 'Continue',

  nameErrorMessage = 'Please enter your name',
  emailErrorMessage = 'Please enter a valid email address',
  phoneErrorMessage = 'Please enter a valid phone number',
  submitError = '',

  isSubmitting = false,

  preview = false,
  lockScroll = true,
  className = '',
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});

  /* ── Reset the form whenever the popup is (re)opened ── */
  useEffect(() => {
    if (preview || !isOpen) return;
    setName('');
    setEmail('');
    setPhone('');
    setErrors({});
  }, [isOpen, preview]);

  const handleSubmit = () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = nameErrorMessage;
    if (!DEFAULT_EMAIL_REGEX.test(email.trim())) nextErrors.email = emailErrorMessage;
    if (!DEFAULT_PHONE_REGEX.test(phone.replace(/\D/g, ''))) nextErrors.phone = phoneErrorMessage;

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit?.({ name: name.trim(), email: email.trim(), phone: phone.trim() });
  };

  const clearError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  return (
    <ModalPopup
      isOpen={isOpen}
      onClose={onClose}
      banner={banner}
      ariaLabel={title}
      preview={preview}
      lockScroll={lockScroll}
      className={`ds-book-meeting ${className}`}
    >
      {/* Top: title + info */}
      <div className="ds-book-meeting__top">
        <h2 className="ds-book-meeting__title typo-h3">{title}</h2>
        {infoText && (
          <p className="ds-book-meeting__info-text typo-body">{infoText}</p>
        )}
      </div>

      {/* Form */}
      <div className="ds-book-meeting__form">
        <div className="ds-book-meeting__inputs">
          <Input
            type="text"
            label={nameLabel}
            icon={<UserRound size={20} />}
            value={name}
            onChange={(e) => { setName(e.target.value); clearError('name'); }}
            error={errors.name}
            autoComplete="name"
          />

          <Input
            type="text"
            label={emailLabel}
            icon={<Mail size={20} />}
            value={email}
            onChange={(e) => { setEmail(e.target.value); clearError('email'); }}
            error={errors.email}
            autoComplete="email"
            inputMode="email"
          />

          <Input
            type="text"
            label={phoneLabel}
            icon={<Phone size={20} />}
            value={phone}
            onChange={(e) => { setPhone(e.target.value); clearError('phone'); }}
            error={errors.phone}
            autoComplete="tel"
            inputMode="tel"
          />
        </div>

        {submitError && (
          <p className="ds-book-meeting__submit-error typo-body-sm" role="alert">
            {submitError}
          </p>
        )}

        <Button
          size="primary"
          variant="main"
          iconRight={<ArrowRight size={20} />}
          onClick={handleSubmit}
          loading={isSubmitting}
          className="ds-book-meeting__submit"
        >
          {submitLabel}
        </Button>
      </div>
    </ModalPopup>
  );
};

BookMeeting.propTypes = {
  /** Whether the popup/drawer is open */
  isOpen: PropTypes.bool,
  /** Callback when the popup is closed */
  onClose: PropTypes.func,
  /** Callback with { name, email, phone } once the form validates */
  onSubmit: PropTypes.func,
  /** Optional banner image (desktop only) */
  banner: PropTypes.string,
  /** Modal title */
  title: PropTypes.string,
  /** Explanatory copy below the title */
  infoText: PropTypes.string,
  /** Name input label */
  nameLabel: PropTypes.string,
  /** Email input label */
  emailLabel: PropTypes.string,
  /** Phone input label */
  phoneLabel: PropTypes.string,
  /** Submit button label */
  submitLabel: PropTypes.string,
  /** Error message for an empty name */
  nameErrorMessage: PropTypes.string,
  /** Error message for an invalid email */
  emailErrorMessage: PropTypes.string,
  /** Error message for an invalid phone number */
  phoneErrorMessage: PropTypes.string,
  /** Form-level error shown above the CTA (owned by the parent) */
  submitError: PropTypes.string,
  /** Shows the DS Button loading spinner while the parent works */
  isSubmitting: PropTypes.bool,
  /** Static preview mode (no animation / portal behaviour) */
  preview: PropTypes.bool,
  /** Lock body scroll while open */
  lockScroll: PropTypes.bool,
  /** Additional class name on the overlay */
  className: PropTypes.string,
};

export default BookMeeting;
